import { AppBar, Toolbar, IconButton, Typography, Box, Stack, Tooltip, Avatar } from '@mui/material'
import MenuIcon from '@mui/icons-material/Menu'
import { useTheme } from '@mui/material/styles'
import { AdminBadge } from './AdminBadge'
import { Logo } from '~/components/Logo/Logo'
import { BRANDING, productLockup } from '~/config/branding'
import { headerGradient, headerAccent } from '~/config/headerBrand'

interface Props {
  user: { email: string }
  drawerOpen: boolean
  onToggle: () => void
}

/** Wave-6 §1: avatar initials — first letters of the email local part's dot/dash/underscore
 *  segments, max 2, uppercase ("greencrate-demo@…" → "GD"). Exported for tests. */
export function emailInitials(email: string): string {
  const local = email.split('@')[0] ?? ''
  const letters = local
    .split(/[._-]+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((p) => p[0]!.toUpperCase())
  return letters.join('') || '?'
}

/** Wave-6 §1 (D2, direction B): the bar wears a left-dark→brand gradient with a 3px
 *  light accent edge — both DERIVED from the tenant brand (theme primary), no hardcoded
 *  hexes, so every tenant gets the treatment mechanically. Two-part wordmark lockup
 *  (name over an eyebrow-style kicker) replaces the single h6; the email caption becomes
 *  an initials avatar with the full address in its tooltip. */
export function TopBar({ user, drawerOpen, onToggle }: Props) {
  const theme = useTheme()
  const brand = theme.palette.primary.main
  const lockup = productLockup(BRANDING.productTitle)
  return (
    <AppBar
      position="fixed"
      sx={{
        zIndex: theme.zIndex.drawer + 1,
        background: headerGradient(brand),
        '&::after': {
          content: '""',
          position: 'absolute',
          left: 0,
          right: 0,
          bottom: 0,
          height: '3px',
          background: headerAccent(brand),
        },
      }}
    >
      <Toolbar>
        <IconButton
          edge="start"
          color="inherit"
          aria-label={drawerOpen ? 'close drawer' : 'open drawer'}
          onClick={onToggle}
          sx={{ mr: 2 }}
        >
          <MenuIcon />
        </IconButton>
        <Logo height={28} />
        <Box sx={{ ml: 1.5, minWidth: 0 }}>
          <Typography
            noWrap
            sx={{ fontSize: 15, fontWeight: 700, letterSpacing: '0.2px', lineHeight: 1.2 }}
          >
            {lockup.name}
          </Typography>
          {lockup.subtitle ? (
            <Typography
              variant="eyebrow"
              noWrap
              data-testid="topbar-subtitle"
              sx={{ display: 'block', opacity: 0.8, lineHeight: 1.2, color: 'inherit' }}
            >
              {lockup.subtitle}
            </Typography>
          ) : null}
        </Box>
        <Box sx={{ flexGrow: 1 }} />
        <Stack direction="row" spacing={1.5} alignItems="center">
          <Tooltip title={user.email}>
            <Avatar
              tabIndex={0}
              aria-label={user.email}
              sx={{
                width: 28,
                height: 28,
                fontSize: 12,
                fontWeight: 600,
                bgcolor: 'rgba(255,255,255,0.14)',
                border: '1px solid rgba(255,255,255,0.35)',
              }}
            >
              {emailInitials(user.email)}
            </Avatar>
          </Tooltip>
          <AdminBadge />
        </Stack>
      </Toolbar>
    </AppBar>
  )
}
